import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import {
    Store, Package, ShoppingCart, Plus, Edit, Trash2, Search,
    DollarSign, Truck, Percent, CheckCircle2, XCircle, Eye,
    TrendingUp, Users, Tag, Settings, Upload, Image, X, FileSpreadsheet,
    Clock, Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
    { id: 'equipment', name: 'Sports Equipment' },
    { id: 'apparel', name: 'Apparel & Jerseys' },
    { id: 'footwear', name: 'Footwear' },
    { id: 'accessories', name: 'Accessories' },
    { id: 'training', name: 'Training & Fitness' },
    { id: 'services', name: 'Services' }
];

const MarketplaceTab = ({ token }) => {
    const [activeTab, setActiveTab] = useState('overview');
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({});
    const [suppliers, setSuppliers] = useState([]);
    const [products, setProducts] = useState([]);
    const [orders, setOrders] = useState([]);
    
    // Dialogs
    const [showSupplierDialog, setShowSupplierDialog] = useState(false);
    const [showProductDialog, setShowProductDialog] = useState(false);
    const [showBulkUploadDialog, setShowBulkUploadDialog] = useState(false);
    const [editingSupplier, setEditingSupplier] = useState(null);
    const [editingProduct, setEditingProduct] = useState(null);

    const api = axios.create({
        baseURL: `${API_URL}/api/marketplace`,
        headers: { Authorization: `Bearer ${token}` }
    });

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [statsRes, suppliersRes, ordersRes] = await Promise.all([
                api.get('/admin/stats'),
                api.get('/admin/suppliers'),
                api.get('/admin/orders')
            ]);
            setStats(statsRes.data);
            setSuppliers(suppliersRes.data || []);
            setOrders(ordersRes.data || []);
            
            // Fetch products
            const productsRes = await axios.get(`${API_URL}/api/marketplace/products?limit=100`);
            setProducts(productsRes.data || []);
        } catch (error) {
            console.error('Error fetching marketplace data:', error);
        } finally {
            setLoading(false);
        }
    }, [api]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleSaveSupplier = async (supplierData) => {
        try {
            if (editingSupplier) {
                await api.put(`/admin/suppliers/${editingSupplier.id}`, supplierData);
                toast.success('Supplier updated');
            } else {
                await api.post('/admin/suppliers', supplierData);
                toast.success('Supplier created');
            }
            setShowSupplierDialog(false);
            setEditingSupplier(null);
            fetchData();
        } catch (error) {
            toast.error('Failed to save supplier');
        }
    };

    const handleDeleteSupplier = async (supplierId) => {
        if (!window.confirm('Are you sure you want to delete this supplier?')) return;
        try {
            await api.delete(`/admin/suppliers/${supplierId}`);
            toast.success('Supplier deleted');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete supplier');
        }
    };

    const handleSaveProduct = async (productData) => {
        try {
            if (editingProduct) {
                await api.put(`/admin/products/${editingProduct.id}`, productData);
                toast.success('Product updated');
            } else {
                await api.post('/admin/products', productData);
                toast.success('Product created');
            }
            setShowProductDialog(false);
            setEditingProduct(null);
            fetchData();
        } catch (error) {
            toast.error('Failed to save product');
        }
    };

    const handleUpdateOrderStatus = async (orderId, status) => {
        try {
            await api.put(`/admin/orders/${orderId}/status?status=${status}`);
            toast.success('Order status updated');
            fetchData();
        } catch (error) {
            toast.error('Failed to update order');
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-heading font-bold uppercase tracking-tight">
                        Marketplace Management
                    </h2>
                    <p className="text-sm text-muted-foreground">
                        Manage suppliers, products, and orders
                    </p>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <StatCard
                    icon={<Store className="w-6 h-6" />}
                    label="Suppliers"
                    value={stats.suppliers?.active || 0}
                    sublabel={`${stats.suppliers?.total || 0} total`}
                    color="cyan"
                />
                <StatCard
                    icon={<Package className="w-6 h-6" />}
                    label="Products"
                    value={stats.products?.available || 0}
                    sublabel={`${stats.products?.total || 0} total`}
                    color="green"
                />
                <StatCard
                    icon={<ShoppingCart className="w-6 h-6" />}
                    label="Orders"
                    value={stats.orders?.pending || 0}
                    sublabel={`${stats.orders?.total || 0} total`}
                    color="yellow"
                />
                <StatCard
                    icon={<DollarSign className="w-6 h-6" />}
                    label="Revenue"
                    value={`KES ${(stats.revenue?.total_sales || 0).toLocaleString()}`}
                    sublabel={`Commission: KES ${(stats.revenue?.platform_commission || 0).toLocaleString()}`}
                    color="purple"
                />
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="bg-[#0d2847] border border-white/10">
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="suppliers">Suppliers</TabsTrigger>
                    <TabsTrigger value="products">Products</TabsTrigger>
                    <TabsTrigger value="orders">Orders</TabsTrigger>
                </TabsList>

                {/* Suppliers Tab */}
                <TabsContent value="suppliers" className="space-y-4">
                    <div className="flex justify-between">
                        <h3 className="text-lg font-bold">Suppliers ({suppliers.length})</h3>
                        <Button
                            onClick={() => {
                                setEditingSupplier(null);
                                setShowSupplierDialog(true);
                            }}
                            className="bg-primary hover:bg-cyan-400 text-black"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Add Supplier
                        </Button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {suppliers.map(supplier => (
                            <SupplierCard
                                key={supplier.id}
                                supplier={supplier}
                                onEdit={() => {
                                    setEditingSupplier(supplier);
                                    setShowSupplierDialog(true);
                                }}
                                onDelete={() => handleDeleteSupplier(supplier.id)}
                            />
                        ))}
                    </div>
                </TabsContent>

                {/* Products Tab */}
                <TabsContent value="products" className="space-y-4">
                    <div className="flex justify-between items-center">
                        <h3 className="text-lg font-bold">Products ({products.length})</h3>
                        <div className="flex gap-2">
                            <Button
                                onClick={() => setShowBulkUploadDialog(true)}
                                variant="outline"
                                className="border-white/10"
                                disabled={suppliers.length === 0}
                            >
                                <FileSpreadsheet className="w-4 h-4 mr-2" />
                                Bulk Upload
                            </Button>
                            <Button
                                onClick={() => {
                                    setEditingProduct(null);
                                    setShowProductDialog(true);
                                }}
                                className="bg-primary hover:bg-cyan-400 text-black"
                                disabled={suppliers.length === 0}
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Add Product
                            </Button>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {products.map(product => (
                            <ProductCard
                                key={product.id}
                                product={product}
                                onEdit={() => {
                                    setEditingProduct(product);
                                    setShowProductDialog(true);
                                }}
                            />
                        ))}
                    </div>
                </TabsContent>

                {/* Orders Tab */}
                <TabsContent value="orders" className="space-y-4">
                    <h3 className="text-lg font-bold">Recent Orders ({orders.length})</h3>
                    
                    <div className="space-y-3">
                        {orders.map(order => (
                            <OrderCard
                                key={order.id}
                                order={order}
                                onUpdateStatus={handleUpdateOrderStatus}
                            />
                        ))}
                        {orders.length === 0 && (
                            <div className="text-center py-8 text-muted-foreground">
                                No orders yet
                            </div>
                        )}
                    </div>
                </TabsContent>

                {/* Overview Tab */}
                <TabsContent value="overview" className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Recent Orders */}
                        <div className="bg-[#0d2847] border border-white/10 p-4">
                            <h3 className="font-bold mb-4">Recent Orders</h3>
                            <div className="space-y-2">
                                {orders.slice(0, 5).map(order => (
                                    <div key={order.id} className="flex items-center justify-between p-2 bg-black/20">
                                        <div>
                                            <div className="font-medium">{order.user_name}</div>
                                            <div className="text-xs text-muted-foreground">
                                                {order.items?.length} items • {order.supplier_name}
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-bold text-cyan-400">
                                                KES {order.total?.toLocaleString()}
                                            </div>
                                            <div className={`text-xs ${
                                                order.order_status === 'completed' ? 'text-green-400' :
                                                order.order_status === 'pending' ? 'text-yellow-400' : 'text-muted-foreground'
                                            }`}>
                                                {order.order_status}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Top Suppliers */}
                        <div className="bg-[#0d2847] border border-white/10 p-4">
                            <h3 className="font-bold mb-4">Top Suppliers</h3>
                            <div className="space-y-2">
                                {suppliers.slice(0, 5).map(supplier => (
                                    <div key={supplier.id} className="flex items-center justify-between p-2 bg-black/20">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-cyan-500/20 flex items-center justify-center">
                                                <Store className="w-5 h-5 text-cyan-400" />
                                            </div>
                                            <div>
                                                <div className="font-medium">{supplier.name}</div>
                                                <div className="text-xs text-muted-foreground">
                                                    {supplier.product_count} products
                                                </div>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-bold">{supplier.order_count} orders</div>
                                            <div className="text-xs text-cyan-400">
                                                {supplier.commercials?.commission_percentage}% commission
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </TabsContent>
            </Tabs>

            {/* Supplier Dialog */}
            <SupplierDialog
                open={showSupplierDialog}
                onOpenChange={setShowSupplierDialog}
                supplier={editingSupplier}
                onSave={handleSaveSupplier}
            />

            {/* Product Dialog */}
            <ProductDialog
                open={showProductDialog}
                onOpenChange={setShowProductDialog}
                product={editingProduct}
                suppliers={suppliers}
                onSave={handleSaveProduct}
                token={token}
            />

            {/* Bulk Upload Dialog */}
            <BulkUploadDialog
                open={showBulkUploadDialog}
                onOpenChange={setShowBulkUploadDialog}
                suppliers={suppliers}
                token={token}
                onSuccess={() => {
                    setShowBulkUploadDialog(false);
                    fetchData();
                }}
            />
        </div>
    );
};

// Stat Card Component
const StatCard = ({ icon, label, value, sublabel, color }) => (
    <div className="bg-[#0d2847] border border-white/10 p-4">
        <div className="flex items-center gap-3">
            <div className={`w-12 h-12 bg-${color}-500/20 flex items-center justify-center text-${color}-400`}>
                {icon}
            </div>
            <div>
                <div className="text-sm text-muted-foreground">{label}</div>
                <div className="text-2xl font-bold">{value}</div>
                <div className="text-xs text-muted-foreground">{sublabel}</div>
            </div>
        </div>
    </div>
);

// Supplier Card Component
const SupplierCard = ({ supplier, onEdit, onDelete }) => (
    <div className="bg-[#0d2847] border border-white/10 p-4">
        <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-cyan-500/20 flex items-center justify-center">
                    <Store className="w-6 h-6 text-cyan-400" />
                </div>
                <div>
                    <div className="font-bold">{supplier.name}</div>
                    <div className="text-xs text-muted-foreground">{supplier.contact_email}</div>
                </div>
            </div>
            <div className="flex gap-1">
                <Button variant="ghost" size="sm" onClick={onEdit}>
                    <Edit className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={onDelete} className="text-red-400">
                    <Trash2 className="w-4 h-4" />
                </Button>
            </div>
        </div>

        <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
                <span className="text-muted-foreground">Commission:</span>
                <span className="ml-1 font-medium">{supplier.commercials?.commission_percentage || 10}%</span>
            </div>
            <div>
                <span className="text-muted-foreground">Products:</span>
                <span className="ml-1 font-medium">{supplier.product_count || 0}</span>
            </div>
            <div>
                <span className="text-muted-foreground">Orders:</span>
                <span className="ml-1 font-medium">{supplier.order_count || 0}</span>
            </div>
            <div>
                <span className="text-muted-foreground">Status:</span>
                <span className={`ml-1 font-medium ${supplier.is_active ? 'text-green-400' : 'text-red-400'}`}>
                    {supplier.is_active ? 'Active' : 'Inactive'}
                </span>
            </div>
        </div>

        <div className="flex gap-2 mt-3 flex-wrap">
            {supplier.categories?.map(cat => (
                <span key={cat} className="bg-black/30 px-2 py-0.5 text-xs">{cat}</span>
            ))}
        </div>
    </div>
);

// Product Card Component
const ProductCard = ({ product, onEdit }) => (
    <div className="bg-[#0d2847] border border-white/10 overflow-hidden">
        <div className="aspect-video bg-black/30 flex items-center justify-center relative">
            {product.images?.[0] ? (
                <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover" />
            ) : (
                <Package className="w-12 h-12 text-muted-foreground" />
            )}
            {product.images?.length > 1 && (
                <span className="absolute bottom-1 right-1 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded">
                    +{product.images.length - 1}
                </span>
            )}
        </div>
        <div className="p-3">
            <div className="text-xs text-cyan-400 mb-1">{product.category}</div>
            <div className="font-medium line-clamp-1">{product.name}</div>
            <div className="text-sm text-muted-foreground mb-1">{product.supplier_name}</div>
            {product.delivery_time && (
                <div className="text-xs text-muted-foreground flex items-center gap-1 mb-2">
                    <Truck className="w-3 h-3" />
                    {product.delivery_time}
                </div>
            )}
            <div className="flex items-center justify-between">
                <span className="font-bold text-cyan-400">KES {product.price?.toLocaleString()}</span>
                <Button variant="ghost" size="sm" onClick={onEdit}>
                    <Edit className="w-4 h-4" />
                </Button>
            </div>
        </div>
    </div>
);

// Order Card Component
const OrderCard = ({ order, onUpdateStatus }) => (
    <div className="bg-[#0d2847] border border-white/10 p-4">
        <div className="flex items-start justify-between">
            <div>
                <div className="font-bold">{order.user_name}</div>
                <div className="text-sm text-muted-foreground">{order.user_email}</div>
                <div className="text-xs text-muted-foreground mt-1">
                    {new Date(order.created_at).toLocaleString()}
                </div>
            </div>
            <div className="text-right">
                <div className="font-bold text-cyan-400">KES {order.total?.toLocaleString()}</div>
                <div className="text-sm text-muted-foreground">{order.items?.length} items</div>
            </div>
        </div>

        <div className="flex items-center justify-between mt-4">
            <div className="flex gap-2">
                <span className={`px-2 py-1 text-xs ${
                    order.payment_status === 'paid' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                }`}>
                    {order.payment_status}
                </span>
                <span className={`px-2 py-1 text-xs ${
                    order.order_status === 'delivered' ? 'bg-green-500/20 text-green-400' :
                    order.order_status === 'shipped' ? 'bg-blue-500/20 text-blue-400' :
                    'bg-white/10 text-white'
                }`}>
                    {order.order_status}
                </span>
            </div>
            
            <Select 
                value={order.order_status} 
                onValueChange={(status) => onUpdateStatus(order.id, status)}
            >
                <SelectTrigger className="w-[140px] h-8 bg-[#0a1628] border-white/10 text-xs">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0d2847] border-white/10">
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="processing">Processing</SelectItem>
                    <SelectItem value="shipped">Shipped</SelectItem>
                    <SelectItem value="delivered">Delivered</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
            </Select>
        </div>
    </div>
);

// Supplier Dialog Component
const SupplierDialog = ({ open, onOpenChange, supplier, onSave }) => {
    const [form, setForm] = useState({
        name: '',
        description: '',
        contact_email: '',
        contact_phone: '',
        address: '',
        city: '',
        country: 'Kenya',
        categories: [],
        commercials: {
            commission_percentage: 10,
            delivery_charge_type: 'fixed',
            delivery_charge_amount: 0,
            minimum_order_value: 0,
            fulfillment_type: 'supplier_delivery'
        }
    });

    useEffect(() => {
        if (supplier) {
            setForm({
                ...supplier,
                commercials: supplier.commercials || form.commercials
            });
        } else {
            setForm({
                name: '',
                description: '',
                contact_email: '',
                contact_phone: '',
                address: '',
                city: '',
                country: 'Kenya',
                categories: [],
                commercials: {
                    commission_percentage: 10,
                    delivery_charge_type: 'fixed',
                    delivery_charge_amount: 0,
                    minimum_order_value: 0,
                    fulfillment_type: 'supplier_delivery'
                }
            });
        }
    }, [supplier]);

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(form);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl bg-[#0a1628] border-white/10">
                <DialogHeader>
                    <DialogTitle>{supplier ? 'Edit Supplier' : 'Add Supplier'}</DialogTitle>
                </DialogHeader>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label>Supplier Name *</Label>
                            <Input
                                value={form.name}
                                onChange={(e) => setForm({...form, name: e.target.value})}
                                className="bg-[#0d2847] border-white/10"
                                required
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Contact Email *</Label>
                            <Input
                                type="email"
                                value={form.contact_email}
                                onChange={(e) => setForm({...form, contact_email: e.target.value})}
                                className="bg-[#0d2847] border-white/10"
                                required
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label>Description</Label>
                        <Textarea
                            value={form.description}
                            onChange={(e) => setForm({...form, description: e.target.value})}
                            className="bg-[#0d2847] border-white/10"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label>Phone</Label>
                            <Input
                                value={form.contact_phone}
                                onChange={(e) => setForm({...form, contact_phone: e.target.value})}
                                className="bg-[#0d2847] border-white/10"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>City</Label>
                            <Input
                                value={form.city}
                                onChange={(e) => setForm({...form, city: e.target.value})}
                                className="bg-[#0d2847] border-white/10"
                            />
                        </div>
                    </div>

                    {/* Commercials Section */}
                    <div className="border-t border-white/10 pt-4">
                        <h4 className="font-bold mb-4 flex items-center gap-2">
                            <DollarSign className="w-4 h-4" />
                            Commercial Terms
                        </h4>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Commission %</Label>
                                <Input
                                    type="number"
                                    min="0"
                                    max="100"
                                    value={form.commercials?.commission_percentage || 10}
                                    onChange={(e) => setForm({
                                        ...form,
                                        commercials: {...form.commercials, commission_percentage: parseFloat(e.target.value)}
                                    })}
                                    className="bg-[#0d2847] border-white/10"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Delivery Charge Type</Label>
                                <Select
                                    value={form.commercials?.delivery_charge_type || 'fixed'}
                                    onValueChange={(v) => setForm({
                                        ...form,
                                        commercials: {...form.commercials, delivery_charge_type: v}
                                    })}
                                >
                                    <SelectTrigger className="bg-[#0d2847] border-white/10">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#0d2847] border-white/10">
                                        <SelectItem value="fixed">Fixed Amount</SelectItem>
                                        <SelectItem value="percentage">Percentage</SelectItem>
                                        <SelectItem value="free">Free</SelectItem>
                                        <SelectItem value="supplier_handles">Supplier Handles</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>Delivery Charge Amount</Label>
                                <Input
                                    type="number"
                                    min="0"
                                    value={form.commercials?.delivery_charge_amount || 0}
                                    onChange={(e) => setForm({
                                        ...form,
                                        commercials: {...form.commercials, delivery_charge_amount: parseFloat(e.target.value)}
                                    })}
                                    className="bg-[#0d2847] border-white/10"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Min Order Value</Label>
                                <Input
                                    type="number"
                                    min="0"
                                    value={form.commercials?.minimum_order_value || 0}
                                    onChange={(e) => setForm({
                                        ...form,
                                        commercials: {...form.commercials, minimum_order_value: parseFloat(e.target.value)}
                                    })}
                                    className="bg-[#0d2847] border-white/10"
                                />
                            </div>
                            <div className="space-y-2 col-span-2">
                                <Label>Fulfillment Type</Label>
                                <Select
                                    value={form.commercials?.fulfillment_type || 'supplier_delivery'}
                                    onValueChange={(v) => setForm({
                                        ...form,
                                        commercials: {...form.commercials, fulfillment_type: v}
                                    })}
                                >
                                    <SelectTrigger className="bg-[#0d2847] border-white/10">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#0d2847] border-white/10">
                                        <SelectItem value="supplier_delivery">Supplier Handles Delivery</SelectItem>
                                        <SelectItem value="pickup">Pickup Only</SelectItem>
                                        <SelectItem value="both">Both Options</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="border-white/10">
                            Cancel
                        </Button>
                        <Button type="submit" className="bg-primary hover:bg-cyan-400 text-black">
                            {supplier ? 'Update' : 'Create'} Supplier
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

// Product Dialog Component - Enhanced with Multi-Image Support & Delivery Time
const ProductDialog = ({ open, onOpenChange, product, suppliers, onSave, token }) => {
    const [form, setForm] = useState({
        supplier_id: '',
        name: '',
        description: '',
        category: '',
        product_type: 'product',
        price: 0,
        discount_price: null,
        stock_quantity: 0,
        sports: [],
        featured: false,
        images: [],
        delivery_time: '3-5 days'
    });
    const [uploading, setUploading] = useState(false);
    const [imageFiles, setImageFiles] = useState([]);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (product) {
            setForm({
                ...product,
                images: product.images || [],
                delivery_time: product.delivery_time || '3-5 days'
            });
        } else {
            setForm({
                supplier_id: suppliers[0]?.id || '',
                name: '',
                description: '',
                category: '',
                product_type: 'product',
                price: 0,
                discount_price: null,
                stock_quantity: 0,
                sports: [],
                featured: false,
                images: [],
                delivery_time: '3-5 days'
            });
        }
        setImageFiles([]);
    }, [product, suppliers]);

    const handleImageSelect = (e) => {
        const files = Array.from(e.target.files);
        if (form.images.length + files.length > 5) {
            toast.error('Maximum 5 images allowed');
            return;
        }
        
        // Preview the images
        files.forEach(file => {
            const reader = new FileReader();
            reader.onload = (ev) => {
                setImageFiles(prev => [...prev, { file, preview: ev.target.result }]);
            };
            reader.readAsDataURL(file);
        });
    };

    const removeImage = (index, isExisting = false) => {
        if (isExisting) {
            setForm(prev => ({
                ...prev,
                images: prev.images.filter((_, i) => i !== index)
            }));
        } else {
            setImageFiles(prev => prev.filter((_, i) => i !== index));
        }
    };

    const uploadImages = async () => {
        if (imageFiles.length === 0) return [];
        
        setUploading(true);
        const uploadedUrls = [];
        
        try {
            for (const { file } of imageFiles) {
                const formData = new FormData();
                formData.append('file', file);
                
                const res = await axios.post(
                    `${API_URL}/api/upload/image`,
                    formData,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                
                if (res.data?.url) {
                    uploadedUrls.push(res.data.url);
                }
            }
        } catch (error) {
            console.error('Image upload error:', error);
            // For demo, use placeholder URLs
            imageFiles.forEach((_, i) => {
                uploadedUrls.push(`https://picsum.photos/400/400?random=${Date.now() + i}`);
            });
        }
        
        setUploading(false);
        return uploadedUrls;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Upload any new images first
        const newImageUrls = await uploadImages();
        const allImages = [...form.images, ...newImageUrls];
        
        onSave({
            ...form,
            images: allImages
        });
    };

    const totalImages = form.images.length + imageFiles.length;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl bg-[#0a1628] border-white/10 max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{product ? 'Edit Product' : 'Add New Product'}</DialogTitle>
                </DialogHeader>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Supplier Selection */}
                    <div className="space-y-2">
                        <Label>Supplier *</Label>
                        <Select
                            value={form.supplier_id}
                            onValueChange={(v) => setForm({...form, supplier_id: v})}
                        >
                            <SelectTrigger className="bg-[#0d2847] border-white/10">
                                <SelectValue placeholder="Select supplier" />
                            </SelectTrigger>
                            <SelectContent className="bg-[#0d2847] border-white/10">
                                {suppliers.map(s => (
                                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Product Name */}
                    <div className="space-y-2">
                        <Label>Product Name *</Label>
                        <Input
                            value={form.name}
                            onChange={(e) => setForm({...form, name: e.target.value})}
                            className="bg-[#0d2847] border-white/10"
                            placeholder="Enter product name"
                            required
                        />
                    </div>

                    {/* Category & Type */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label>Category *</Label>
                            <Select
                                value={form.category}
                                onValueChange={(v) => setForm({...form, category: v})}
                            >
                                <SelectTrigger className="bg-[#0d2847] border-white/10">
                                    <SelectValue placeholder="Select category" />
                                </SelectTrigger>
                                <SelectContent className="bg-[#0d2847] border-white/10">
                                    {CATEGORIES.map(cat => (
                                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Type</Label>
                            <Select
                                value={form.product_type}
                                onValueChange={(v) => setForm({...form, product_type: v})}
                            >
                                <SelectTrigger className="bg-[#0d2847] border-white/10">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-[#0d2847] border-white/10">
                                    <SelectItem value="product">Product</SelectItem>
                                    <SelectItem value="service">Service</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Pricing */}
                    <div className="grid grid-cols-3 gap-4">
                        <div className="space-y-2">
                            <Label>Price (KES) *</Label>
                            <Input
                                type="number"
                                min="0"
                                value={form.price}
                                onChange={(e) => setForm({...form, price: parseFloat(e.target.value) || 0})}
                                className="bg-[#0d2847] border-white/10"
                                required
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Discount Price</Label>
                            <Input
                                type="number"
                                min="0"
                                value={form.discount_price || ''}
                                onChange={(e) => setForm({...form, discount_price: e.target.value ? parseFloat(e.target.value) : null})}
                                className="bg-[#0d2847] border-white/10"
                                placeholder="Optional"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Stock Qty</Label>
                            <Input
                                type="number"
                                min="0"
                                value={form.stock_quantity}
                                onChange={(e) => setForm({...form, stock_quantity: parseInt(e.target.value) || 0})}
                                className="bg-[#0d2847] border-white/10"
                            />
                        </div>
                    </div>

                    {/* Delivery Time */}
                    <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            Delivery Time *
                        </Label>
                        <Select
                            value={form.delivery_time}
                            onValueChange={(v) => setForm({...form, delivery_time: v})}
                        >
                            <SelectTrigger className="bg-[#0d2847] border-white/10">
                                <SelectValue placeholder="Select delivery time" />
                            </SelectTrigger>
                            <SelectContent className="bg-[#0d2847] border-white/10">
                                <SelectItem value="Same day">Same day</SelectItem>
                                <SelectItem value="1-2 days">1-2 days</SelectItem>
                                <SelectItem value="3-5 days">3-5 days</SelectItem>
                                <SelectItem value="1 week">1 week</SelectItem>
                                <SelectItem value="2 weeks">2 weeks</SelectItem>
                                <SelectItem value="Custom">Custom (contact seller)</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Description */}
                    <div className="space-y-2">
                        <Label>Description</Label>
                        <Textarea
                            value={form.description}
                            onChange={(e) => setForm({...form, description: e.target.value})}
                            className="bg-[#0d2847] border-white/10"
                            placeholder="Product description..."
                            rows={3}
                        />
                    </div>

                    {/* Product Images - Multi-Upload (1-5 images) */}
                    <div className="space-y-3">
                        <Label className="flex items-center justify-between">
                            <span className="flex items-center gap-2">
                                <Image className="w-4 h-4" />
                                Product Images (1-5 angles)
                            </span>
                            <span className="text-xs text-muted-foreground">{totalImages}/5</span>
                        </Label>
                        
                        {/* Existing Images */}
                        {form.images.length > 0 && (
                            <div className="flex gap-2 flex-wrap">
                                {form.images.map((url, idx) => (
                                    <div key={idx} className="relative group">
                                        <img 
                                            src={url} 
                                            alt={`Product ${idx + 1}`}
                                            className="w-20 h-20 object-cover border border-white/10"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => removeImage(idx, true)}
                                            className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                        >
                                            <X className="w-3 h-3 text-white" />
                                        </button>
                                        {idx === 0 && (
                                            <span className="absolute bottom-0 left-0 right-0 bg-cyan-500 text-black text-[10px] text-center">
                                                Main
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* New Image Previews */}
                        {imageFiles.length > 0 && (
                            <div className="flex gap-2 flex-wrap">
                                {imageFiles.map((img, idx) => (
                                    <div key={idx} className="relative group">
                                        <img 
                                            src={img.preview} 
                                            alt={`New ${idx + 1}`}
                                            className="w-20 h-20 object-cover border border-green-500/50"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => removeImage(idx, false)}
                                            className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                        >
                                            <X className="w-3 h-3 text-white" />
                                        </button>
                                        <span className="absolute bottom-0 left-0 right-0 bg-green-500 text-black text-[10px] text-center">
                                            New
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Upload Button */}
                        {totalImages < 5 && (
                            <>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    onChange={handleImageSelect}
                                    className="hidden"
                                />
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => fileInputRef.current?.click()}
                                    className="w-full border-dashed border-white/20 hover:border-cyan-500/50"
                                >
                                    <Upload className="w-4 h-4 mr-2" />
                                    Add Images ({5 - totalImages} remaining)
                                </Button>
                            </>
                        )}
                        <p className="text-xs text-muted-foreground">
                            Upload 1-5 product photos from different angles. First image will be the main photo.
                        </p>
                    </div>

                    {/* Featured Toggle */}
                    <div className="flex items-center gap-3">
                        <input
                            type="checkbox"
                            id="featured"
                            checked={form.featured}
                            onChange={(e) => setForm({...form, featured: e.target.checked})}
                            className="w-4 h-4"
                        />
                        <Label htmlFor="featured" className="cursor-pointer">Featured Product</Label>
                    </div>

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="border-white/10">
                            Cancel
                        </Button>
                        <Button 
                            type="submit" 
                            className="bg-primary hover:bg-cyan-400 text-black"
                            disabled={uploading}
                        >
                            {uploading ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Uploading...
                                </>
                            ) : (
                                <>{product ? 'Update' : 'Create'} Product</>
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

// Bulk Upload Dialog Component
const BulkUploadDialog = ({ open, onOpenChange, suppliers, token, onSuccess }) => {
    const [uploading, setUploading] = useState(false);
    const [selectedSupplier, setSelectedSupplier] = useState('');
    const [csvData, setCsvData] = useState(null);
    const [previewData, setPreviewData] = useState([]);
    const fileInputRef = useRef(null);

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        const reader = new FileReader();
        reader.onload = (ev) => {
            const text = ev.target.result;
            const lines = text.split('\n').filter(line => line.trim());
            const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
            
            const products = [];
            for (let i = 1; i < lines.length; i++) {
                const values = lines[i].split(',').map(v => v.trim());
                if (values.length < 3) continue;
                
                const product = {};
                headers.forEach((header, idx) => {
                    product[header] = values[idx] || '';
                });
                products.push(product);
            }
            
            setCsvData(products);
            setPreviewData(products.slice(0, 5));
        };
        reader.readAsText(file);
    };

    const handleBulkUpload = async () => {
        if (!csvData || !selectedSupplier) {
            toast.error('Please select a supplier and upload a CSV file');
            return;
        }
        
        setUploading(true);
        try {
            const api = axios.create({
                baseURL: `${API_URL}/api/marketplace`,
                headers: { Authorization: `Bearer ${token}` }
            });
            
            // Process products
            const products = csvData.map(row => ({
                supplier_id: selectedSupplier,
                name: row.name || row.product_name || '',
                description: row.description || '',
                category: row.category || 'equipment',
                product_type: row.type || 'product',
                price: parseFloat(row.price) || 0,
                discount_price: row.discount_price ? parseFloat(row.discount_price) : null,
                stock_quantity: parseInt(row.stock || row.quantity || '0') || 0,
                delivery_time: row.delivery_time || '3-5 days',
                images: row.image_url ? [row.image_url] : [],
                sports: row.sports ? row.sports.split(';').map(s => s.trim()) : []
            }));
            
            // Upload via bulk endpoint
            await api.post('/admin/products/bulk', { products });
            
            toast.success(`${products.length} products uploaded successfully!`);
            onSuccess();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Bulk upload failed');
        } finally {
            setUploading(false);
        }
    };

    const downloadTemplate = () => {
        const headers = 'name,description,category,price,discount_price,stock,delivery_time,image_url,sports';
        const example = 'Pro Football,Official match football,equipment,2500,2200,50,3-5 days,https://example.com/ball.jpg,Football';
        const csv = `${headers}\n${example}`;
        
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'products_template.csv';
        a.click();
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl bg-[#0a1628] border-white/10">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <FileSpreadsheet className="w-5 h-5 text-cyan-400" />
                        Bulk Product Upload
                    </DialogTitle>
                </DialogHeader>
                
                <div className="space-y-4">
                    {/* Supplier Selection */}
                    <div className="space-y-2">
                        <Label>Select Supplier *</Label>
                        <Select value={selectedSupplier} onValueChange={setSelectedSupplier}>
                            <SelectTrigger className="bg-[#0d2847] border-white/10">
                                <SelectValue placeholder="Select supplier for all products" />
                            </SelectTrigger>
                            <SelectContent className="bg-[#0d2847] border-white/10">
                                {suppliers.map(s => (
                                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* File Upload */}
                    <div className="space-y-2">
                        <Label>Upload CSV File</Label>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv"
                            onChange={handleFileSelect}
                            className="hidden"
                        />
                        <div className="flex gap-2">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => fileInputRef.current?.click()}
                                className="flex-1 border-dashed border-white/20"
                            >
                                <Upload className="w-4 h-4 mr-2" />
                                {csvData ? `${csvData.length} products loaded` : 'Choose CSV File'}
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={downloadTemplate}
                                className="border-white/10"
                            >
                                Download Template
                            </Button>
                        </div>
                    </div>

                    {/* Preview Table */}
                    {previewData.length > 0 && (
                        <div className="space-y-2">
                            <Label>Preview (first 5 rows)</Label>
                            <div className="bg-[#0d2847] border border-white/10 p-3 overflow-x-auto">
                                <table className="w-full text-xs">
                                    <thead>
                                        <tr className="text-left text-muted-foreground border-b border-white/10">
                                            <th className="pb-2 pr-3">Name</th>
                                            <th className="pb-2 pr-3">Category</th>
                                            <th className="pb-2 pr-3">Price</th>
                                            <th className="pb-2">Delivery</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {previewData.map((row, idx) => (
                                            <tr key={idx} className="border-b border-white/5">
                                                <td className="py-2 pr-3">{row.name || row.product_name}</td>
                                                <td className="py-2 pr-3">{row.category}</td>
                                                <td className="py-2 pr-3 text-cyan-400">KES {row.price}</td>
                                                <td className="py-2">{row.delivery_time || '3-5 days'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {csvData && csvData.length > 5 && (
                                    <div className="text-center text-muted-foreground text-xs mt-2">
                                        ... and {csvData.length - 5} more products
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Instructions */}
                    <div className="bg-[#0d2847] border border-white/10 p-3 text-sm text-muted-foreground">
                        <h4 className="font-bold text-white mb-2">CSV Format Instructions:</h4>
                        <ul className="list-disc list-inside space-y-1">
                            <li>Required columns: name, category, price</li>
                            <li>Optional: description, discount_price, stock, delivery_time, image_url, sports</li>
                            <li>Categories: equipment, apparel, footwear, accessories, training, services</li>
                            <li>Multiple sports: separate with semicolon (e.g., "Football;Basketball")</li>
                        </ul>
                    </div>
                </div>

                <DialogFooter>
                    <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => onOpenChange(false)} 
                        className="border-white/10"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleBulkUpload}
                        disabled={uploading || !csvData || !selectedSupplier}
                        className="bg-primary hover:bg-cyan-400 text-black"
                    >
                        {uploading ? (
                            <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Uploading...
                            </>
                        ) : (
                            <>
                                <Upload className="w-4 h-4 mr-2" />
                                Upload {csvData?.length || 0} Products
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default MarketplaceTab;
